import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { centsToInput, formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import { beneficiosDoVinculo, type BeneficioCatalogo, type BeneficiosDoColaborador } from "@/data/beneficios";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

interface Props {
  usuario: User | null;
  onClose: () => void;
}

/**
 * Como maskReaisInput, mas aceita zero: aqui "0,00" é uma escolha (não recebe
 * o fixo), diferente de campo vazio (segue o fixo).
 */
const maskAceitaZero = (input: string) => {
  const digitos = input.replace(/\D/g, "");
  if (!digitos) return "";
  return /^0+$/.test(digitos) ? "0,00" : maskReaisInput(input);
};

const centavos = (texto: string) => Math.round(parseReaisInput(texto) * 100);

/** Benefícios padrão de um colaborador: valor próprio tem prioridade sobre o fixo do vínculo. */
export const BeneficiosColaboradorModal = ({ usuario, onClose }: Props) => {
  const [catalogo, setCatalogo] = useState<BeneficioCatalogo[]>([]);
  const [dados, setDados] = useState<BeneficiosDoColaborador | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  // O pai passa onClose inline; guardar em ref evita refazer a busca a cada render.
  const fechar = useRef(onClose);
  fechar.current = onClose;
  const usuarioId = usuario?.id;

  useEffect(() => {
    if (!usuarioId) return;
    setDados(null);
    Promise.all([
      api.get<BeneficioCatalogo[]>("/beneficios/catalogo"),
      api.get<BeneficiosDoColaborador>(`/beneficios/colaboradores/${usuarioId}`),
    ])
      .then(([lista, d]) => {
        // O complemento de salário vem do cadastro; aqui só os que têm valor padrão.
        setCatalogo(beneficiosDoVinculo(lista, d.colaborador.vinculo, { incluirSistema: false }));
        setDados(d);
        setValores(Object.fromEntries(Object.entries(d.proprios).map(([tipo, c]) => [tipo, centsToInput(c)])));
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : "Erro ao carregar benefícios");
        fechar.current();
      });
  }, [usuarioId]);

  /** O que o colaborador vai receber nas próximas folhas. */
  const efetivo = (tipo: string) => {
    const texto = valores[tipo] ?? "";
    if (texto.trim()) {
      try {
        return centavos(texto);
      } catch {
        return 0;
      }
    }
    return dados?.fixos[tipo] ?? 0;
  };

  const total = catalogo.reduce((s, b) => s + efetivo(b.tipo), 0) + (dados?.complementoSalario ?? 0);

  const salvar = async () => {
    if (!usuario) return;
    const payload: Record<string, number> = {};
    for (const b of catalogo) {
      const texto = valores[b.tipo] ?? "";
      if (!texto.trim()) continue;
      try {
        payload[b.tipo] = parseReaisInput(texto);
      } catch {
        toast.error(`Valor inválido em ${b.nome}`);
        return;
      }
    }

    setSalvando(true);
    try {
      await api.put(`/beneficios/colaboradores/${usuario.id}`, { valores: payload });
      toast.success("Benefícios do colaborador salvos. Valem para as próximas folhas de benefícios.");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  const vinculo = dados?.colaborador.vinculo === "clt" ? "CLT" : "PJ";

  return (
    <Dialog open={!!usuario} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Benefícios — {usuario?.name}</DialogTitle>
          <DialogDescription>
            Em branco, o colaborador recebe o fixo de {vinculo} definido em Configurações (aparece no
            campo). Um valor preenchido tem prioridade sobre o fixo, inclusive <strong>0</strong>, que
            significa não receber aquele benefício. Vale para as próximas folhas de benefícios.
          </DialogDescription>
        </DialogHeader>

        {dados && dados.complementoSalario > 0 && (
          <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            Complemento de salário de <strong>{formatCents(dados.complementoSalario)}</strong> entra em
            toda folha de benefícios (definido em Editar cadastro).
          </p>
        )}

        {!dados ? (
          <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-muted-foreground" />
        ) : catalogo.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum benefício cadastrado para {vinculo}. Cadastre em Configurações → Benefícios.
          </p>
        ) : (
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            {catalogo.map((b) => {
              const fixo = dados.fixos[b.tipo] ?? 0;
              const proprio = (valores[b.tipo] ?? "").trim() !== "";
              return (
                <div key={b.tipo} className="space-y-1">
                  <Label htmlFor={`colab-${b.tipo}`} className="flex items-center gap-1 text-xs">
                    {b.nome}
                    {proprio && <span className="font-normal text-primary">· valor próprio</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      id={`colab-${b.tipo}`}
                      value={valores[b.tipo] ?? ""}
                      onChange={(e) => setValores((v) => ({ ...v, [b.tipo]: maskAceitaZero(e.target.value) }))}
                      inputMode="numeric"
                      placeholder={fixo > 0 ? `Fixo: ${centsToInput(fixo)}` : "—"}
                      className="h-9 pr-8"
                    />
                    {proprio && (
                      <button
                        type="button"
                        onClick={() => setValores((v) => ({ ...v, [b.tipo]: "" }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={`Voltar ${b.nome} ao fixo`}
                        title="Voltar ao fixo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="items-center sm:justify-between">
          <p className="text-sm">
            Recebe por folha: <strong className="tabular-nums">{formatCents(total)}</strong>
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} disabled={salvando || !dados}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
